export const STAGE_PADDING_PX = 32
export const MIN_CROP_CSS_PX = 64
export const HANDLE_HIT_PX = 48
export const MAX_ZOOM_FACTOR = 8
export const ZOOM_STEP = 1.18
export const JPEG_QUALITY = 0.95
export const MAX_OUTPUT_PIXELS = 16 * 1024 * 1024
export const CSS_EDGE_SNAP_PX = 0.5

export const CROP_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

export function normalizeRotation(rotation) {
  const rounded = Math.round(Number(rotation) / 90) * 90
  return ((rounded % 360) + 360) % 360
}

export function orientedSize(naturalWidth, naturalHeight, rotation) {
  const width = Math.max(0, Number(naturalWidth) || 0)
  const height = Math.max(0, Number(naturalHeight) || 0)
  const r = normalizeRotation(rotation)
  if (r === 90 || r === 270) {
    return { width: height, height: width }
  }
  return { width, height }
}

export function rotateSize(width, height, rotation) {
  const r = normalizeRotation(rotation)
  if (r === 90 || r === 270) {
    return { width: height, height: width }
  }
  if (r === 0 || r === 180) {
    return { width, height }
  }
  const rotRad = (r * Math.PI) / 180
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height)
  }
}

export function imageRect(image, orientedWidth, orientedHeight) {
  return {
    x: image.x,
    y: image.y,
    width: orientedWidth * image.scale,
    height: orientedHeight * image.scale
  }
}

export function intersectRects(a, b) {
  const x = Math.max(a.x, b.x)
  const y = Math.max(a.y, b.y)
  const right = Math.min(a.x + a.width, b.x + b.width)
  const bottom = Math.min(a.y + a.height, b.y + b.height)
  return {
    x,
    y,
    width: Math.max(0, right - x),
    height: Math.max(0, bottom - y)
  }
}

export function paddedStageRect(stageWidth, stageHeight, padding = STAGE_PADDING_PX) {
  const pad = Math.max(0, padding)
  return {
    x: pad,
    y: pad,
    width: Math.max(1, stageWidth - pad * 2),
    height: Math.max(1, stageHeight - pad * 2)
  }
}

export function minScaleToCover(crop, orientedWidth, orientedHeight) {
  if (!orientedWidth || !orientedHeight || !crop) return 0
  return Math.max(crop.width / orientedWidth, crop.height / orientedHeight)
}

export function minCropSize(image, orientedWidth, orientedHeight) {
  const rect = imageRect(image, orientedWidth, orientedHeight)
  return Math.max(1, Math.min(MIN_CROP_CSS_PX, rect.width, rect.height))
}

export function clampCropToBounds(crop, bounds, minSize) {
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
    return { ...crop }
  }
  const minW = Math.min(Math.max(1, minSize), bounds.width)
  const minH = Math.min(Math.max(1, minSize), bounds.height)
  const width = clamp(crop.width, minW, bounds.width)
  const height = clamp(crop.height, minH, bounds.height)
  const x = clamp(crop.x, bounds.x, bounds.x + bounds.width - width)
  const y = clamp(crop.y, bounds.y, bounds.y + bounds.height - height)
  return { x, y, width, height }
}

export function clampImageToCoverCrop(image, crop, orientedWidth, orientedHeight) {
  const width = orientedWidth * image.scale
  const height = orientedHeight * image.scale
  let x = image.x
  let y = image.y

  if (width <= crop.width) {
    x = crop.x + (crop.width - width) / 2
  } else {
    x = clamp(x, crop.x + crop.width - width, crop.x)
  }

  if (height <= crop.height) {
    y = crop.y + (crop.height - height) / 2
  } else {
    y = clamp(y, crop.y + crop.height - height, crop.y)
  }

  return { x, y, scale: image.scale }
}

export function applyScaleAround(image, nextScale, focalX, focalY, orientedWidth, orientedHeight) {
  const width = orientedWidth * image.scale
  const height = orientedHeight * image.scale
  const relX = width === 0 ? 0.5 : (focalX - image.x) / width
  const relY = height === 0 ? 0.5 : (focalY - image.y) / height
  const nextWidth = orientedWidth * nextScale
  const nextHeight = orientedHeight * nextScale
  return {
    x: focalX - relX * nextWidth,
    y: focalY - relY * nextHeight,
    scale: nextScale
  }
}

export function ensureImageCoversCrop(image, crop, orientedWidth, orientedHeight, maxScale) {
  const minScale = minScaleToCover(crop, orientedWidth, orientedHeight)
  const targetScale = clamp(image.scale, minScale, Math.max(minScale, maxScale))
  let next = image
  if (targetScale !== image.scale) {
    next = applyScaleAround(
      image,
      targetScale,
      crop.x + crop.width / 2,
      crop.y + crop.height / 2,
      orientedWidth,
      orientedHeight
    )
  }
  return clampImageToCoverCrop(next, crop, orientedWidth, orientedHeight)
}

export function computeContainLayout(stageWidth, stageHeight, orientedWidth, orientedHeight, padding = STAGE_PADDING_PX) {
  const safeOrientedW = Math.max(1, orientedWidth)
  const safeOrientedH = Math.max(1, orientedHeight)
  const avail = paddedStageRect(stageWidth, stageHeight, padding)
  const scale = Math.min(avail.width / safeOrientedW, avail.height / safeOrientedH)
  const width = safeOrientedW * scale
  const height = safeOrientedH * scale
  const x = (stageWidth - width) / 2
  const y = (stageHeight - height) / 2
  const image = { x, y, scale }
  const crop = { x, y, width, height }
  return {
    image,
    crop,
    containScale: scale,
    stageWidth,
    stageHeight
  }
}

export function maxScaleForLayout(containScale) {
  return Math.max(containScale * MAX_ZOOM_FACTOR, containScale)
}

export function cropBoundsForImage(image, orientedWidth, orientedHeight, stageWidth, stageHeight, padding = STAGE_PADDING_PX) {
  return intersectRects(
    imageRect(image, orientedWidth, orientedHeight),
    paddedStageRect(stageWidth, stageHeight, padding)
  )
}

export function resizeCropRect(originCrop, handle, dx, dy, minSize, bounds) {
  let x = originCrop.x
  let y = originCrop.y
  let width = originCrop.width
  let height = originCrop.height

  if (handle.indexOf('e') !== -1) {
    width = originCrop.width + dx
  }
  if (handle.indexOf('w') !== -1) {
    x = originCrop.x + dx
    width = originCrop.width - dx
  }
  if (handle.indexOf('s') !== -1) {
    height = originCrop.height + dy
  }
  if (handle.indexOf('n') !== -1) {
    y = originCrop.y + dy
    height = originCrop.height - dy
  }

  if (width < minSize) {
    if (handle.indexOf('w') !== -1) {
      x = originCrop.x + originCrop.width - minSize
    }
    width = minSize
  }
  if (height < minSize) {
    if (handle.indexOf('n') !== -1) {
      y = originCrop.y + originCrop.height - minSize
    }
    height = minSize
  }

  return clampCropToBounds({ x, y, width, height }, bounds, minSize)
}

export function moveCropRect(originCrop, dx, dy, bounds, minSize) {
  return clampCropToBounds(
    {
      x: originCrop.x + dx,
      y: originCrop.y + dy,
      width: originCrop.width,
      height: originCrop.height
    },
    bounds,
    minSize
  )
}

export function expandAndClampRect(rect, boundWidth, boundHeight) {
  const maxW = Math.max(0, boundWidth)
  const maxH = Math.max(0, boundHeight)
  if (maxW < 1 || maxH < 1) {
    return { x: 0, y: 0, width: 0, height: 0 }
  }

  let left = Math.floor(rect.x + 1e-6)
  let top = Math.floor(rect.y + 1e-6)
  let right = Math.ceil(rect.x + rect.width - 1e-6)
  let bottom = Math.ceil(rect.y + rect.height - 1e-6)

  left = clamp(left, 0, maxW - 1)
  top = clamp(top, 0, maxH - 1)
  right = clamp(right, left + 1, maxW)
  bottom = clamp(bottom, top + 1, maxH)

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top
  }
}

export function pixelCropFromLayout({
  crop,
  imageX,
  imageY,
  scale,
  naturalWidth,
  naturalHeight,
  rotation
}) {
  const oriented = orientedSize(naturalWidth, naturalHeight, rotation)
  if (!scale || oriented.width < 1 || oriented.height < 1) {
    return { x: 0, y: 0, width: 0, height: 0 }
  }

  let srcX = (crop.x - imageX) / scale
  let srcY = (crop.y - imageY) / scale
  let srcW = crop.width / scale
  let srcH = crop.height / scale

  if (Math.abs(crop.x - imageX) < CSS_EDGE_SNAP_PX) srcX = 0
  if (Math.abs(crop.y - imageY) < CSS_EDGE_SNAP_PX) srcY = 0

  const imageRight = imageX + oriented.width * scale
  const imageBottom = imageY + oriented.height * scale
  const cropRight = crop.x + crop.width
  const cropBottom = crop.y + crop.height
  if (Math.abs(imageRight - cropRight) < CSS_EDGE_SNAP_PX) {
    srcW = oriented.width - srcX
  }
  if (Math.abs(imageBottom - cropBottom) < CSS_EDGE_SNAP_PX) {
    srcH = oriented.height - srcY
  }

  return expandAndClampRect(
    { x: srcX, y: srcY, width: srcW, height: srcH },
    oriented.width,
    oriented.height
  )
}

export function isFullImageCrop(pixelCrop, orientedWidth, orientedHeight) {
  if (!pixelCrop || orientedWidth < 1 || orientedHeight < 1) return false
  return (
    pixelCrop.x <= 0 &&
    pixelCrop.y <= 0 &&
    pixelCrop.x + pixelCrop.width >= orientedWidth &&
    pixelCrop.y + pixelCrop.height >= orientedHeight
  )
}

export function relayoutPreservingView(prev, stageWidth, stageHeight, orientedWidth, orientedHeight, padding = STAGE_PADDING_PX) {
  const nextContain = computeContainLayout(stageWidth, stageHeight, orientedWidth, orientedHeight, padding)
  if (!prev || !prev.image || prev.stageWidth < 1 || prev.stageHeight < 1 || !prev.containScale) {
    return nextContain
  }

  const prevImgW = orientedWidth * prev.image.scale
  const prevImgH = orientedHeight * prev.image.scale
  if (prevImgW < 1 || prevImgH < 1) {
    return nextContain
  }

  const zoomFactor = prev.image.scale / prev.containScale
  const scale = nextContain.containScale * zoomFactor
  const cropRel = {
    x: (prev.crop.x - prev.image.x) / prevImgW,
    y: (prev.crop.y - prev.image.y) / prevImgH,
    width: prev.crop.width / prevImgW,
    height: prev.crop.height / prevImgH
  }

  const focalImgX = (prev.crop.x + prev.crop.width / 2 - prev.image.x) / prevImgW
  const focalImgY = (prev.crop.y + prev.crop.height / 2 - prev.image.y) / prevImgH
  const newFocalX = ((prev.crop.x + prev.crop.width / 2) / prev.stageWidth) * stageWidth
  const newFocalY = ((prev.crop.y + prev.crop.height / 2) / prev.stageHeight) * stageHeight
  const nextImgW = orientedWidth * scale
  const nextImgH = orientedHeight * scale

  let image = {
    x: newFocalX - focalImgX * nextImgW,
    y: newFocalY - focalImgY * nextImgH,
    scale
  }
  let crop = {
    x: image.x + cropRel.x * nextImgW,
    y: image.y + cropRel.y * nextImgH,
    width: cropRel.width * nextImgW,
    height: cropRel.height * nextImgH
  }

  const maxScale = maxScaleForLayout(nextContain.containScale)
  const minSize = minCropSize(image, orientedWidth, orientedHeight)
  const bounds = cropBoundsForImage(image, orientedWidth, orientedHeight, stageWidth, stageHeight, padding)
  crop = clampCropToBounds(crop, bounds, minSize)
  image = ensureImageCoversCrop(image, crop, orientedWidth, orientedHeight, maxScale)

  return {
    image,
    crop,
    containScale: nextContain.containScale,
    stageWidth,
    stageHeight
  }
}

/**
 * Maps an axis-aligned crop in oriented (rotated) pixel space back to source
 * pixels. Used for tests and documentation of 90-degree mappings.
 */
export function orientedCropToSource(crop, naturalWidth, naturalHeight, rotation) {
  const r = normalizeRotation(rotation)
  const x = crop.x
  const y = crop.y
  const width = crop.width
  const height = crop.height

  if (r === 0) {
    return { x, y, width, height }
  }
  if (r === 90) {
    return {
      x: y,
      y: naturalHeight - x - width,
      width: height,
      height: width
    }
  }
  if (r === 180) {
    return {
      x: naturalWidth - x - width,
      y: naturalHeight - y - height,
      width,
      height
    }
  }
  return {
    x: naturalWidth - y - height,
    y: x,
    width: height,
    height: width
  }
}
